import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface InputMappingEditorProps {
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  error?: string;
}

const TEMPLATE_REFERENCE = [
  { template: '{{channel}}', description: 'Channel name the message was published to' },
  { template: '{{entityId}}', description: 'ID of the entity in the message' },
  { template: '{{entity}}', description: 'Entity type (e.g., "Contact")' },
  { template: '{{operation}}', description: 'Operation name (e.g., "create")' },
  { template: '{{source}}', description: 'Source system identifier' },
  { template: '{{projectId}}', description: 'Project ID extracted from channel or message' },
  { template: '{{payload.fieldName}}', description: 'Dot-path access into the message payload' },
  { template: '{{meta.fieldName}}', description: 'Dot-path access into message metadata' },
];

export function InputMappingEditor({ value, onChange, error }: InputMappingEditorProps) {
  const [jsonText, setJsonText] = useState(() => {
    if (Object.keys(value).length === 0) return '{}';
    return JSON.stringify(value, null, 2);
  });
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);

  const handleBlur = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
        setJsonError('Input mapping must be a JSON object');
        return;
      }
      setJsonError(null);
      onChange(parsed as Record<string, unknown>);
    } catch {
      setJsonError('Invalid JSON — please check the syntax');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonText(e.target.value);
    // Clear error while typing
    if (jsonError) setJsonError(null);
  };

  const displayError = jsonError || error;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Input Mapping
      </label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
        A JSON object mapping endpoint input fields to message values. Use{' '}
        <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-mono text-xs">
          {'{{template}}'}
        </code>{' '}
        syntax to reference message fields, or provide static values.
      </p>
      <textarea
        value={jsonText}
        onChange={handleChange}
        onBlur={handleBlur}
        rows={8}
        spellCheck={false}
        className={`block w-full px-3 py-2 border rounded-md shadow-sm sm:text-sm font-mono
          ${displayError
            ? 'border-red-300 dark:border-red-600 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-blue-500'
          }
          bg-white dark:bg-gray-700
          text-gray-900 dark:text-white
          focus:outline-none focus:ring-1
        `}
      />
      {displayError && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{displayError}</p>
      )}

      {/* Available Templates Reference */}
      <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
        <button
          type="button"
          onClick={() => setShowTemplates(!showTemplates)}
          className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <span className="font-medium">Available Templates</span>
          {showTemplates ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
        </button>
        {showTemplates && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Template</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {TEMPLATE_REFERENCE.map(({ template, description }) => (
                  <tr key={template}>
                    <td className="px-3 py-2 font-mono text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                      {template}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                      {description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
